import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Card, Description } from '@heroui/react';

export type StatusCalloutStatus = 'warning' | 'danger' | 'success' | 'info';

const STATUS_CONFIG = {
  warning: {
    border: 'border-warning',
    bg: 'bg-warning/10',
    Icon: AlertTriangle,
    iconClass: 'text-warning',
  },
  danger: {
    border: 'border-danger',
    bg: 'bg-danger/10',
    Icon: AlertCircle,
    iconClass: 'text-danger',
  },
  success: {
    border: 'border-success',
    bg: 'bg-success/10',
    Icon: CheckCircle2,
    iconClass: 'text-success',
  },
  info: {
    border: 'border-accent',
    bg: 'bg-accent/10',
    Icon: Info,
    iconClass: 'text-accent',
  },
} as const;

interface StatusCalloutProps {
  status: StatusCalloutStatus;
  title?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function StatusCallout({
  status,
  title,
  children,
  action,
  className = '',
}: StatusCalloutProps) {
  const { border, bg, Icon, iconClass } = STATUS_CONFIG[status];

  return (
    <Card className={`border ${border} ${bg} ${className}`.trim()}>
      <Card.Content className="flex gap-3 p-4">
        <Icon className={`mt-0.5 shrink-0 ${iconClass}`} size={20} />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {title ? <Description className="font-medium text-foreground">{title}</Description> : null}
          {typeof children === 'string' ? <Description>{children}</Description> : children}
          {action}
        </div>
      </Card.Content>
    </Card>
  );
}
